import { useEffect, useState } from "react";
import { localize } from "../../common/services/localization";
import { PageContainer } from "../components/layout/page";
import { SearchInput, SelectInput, SelectWithSearchInput } from "../components/functional/inputs";
import { BlueButton, OrangeButton, PaginationButtons } from "../components/functional/buttons";
import { PlayersRepository } from "../../api/repositories/stats/players";
import { PlayerShort } from "../../api/repositories/stats/models/players";
import { SubscribeGlobalState } from "../../common/hooks/global-state";
import { MatchesRepository } from "../../api/repositories/stats/matches";
import { MatchSearchFilters, ShortMatchModel } from "../../api/repositories/stats/models/matches";
import { HeroImage } from "../components/ui/file-contents";
import { toDate, toDateWithTime } from "../../common/utils";

export const MatchesSearch: React.FC = () => {
    return <PageContainer
        staticBg={true}
        containerClassName='mt-[60px] flex flex-col justify-center items-center'
        headerImgPath='/v2/images/bg/towers.png'
        child={
            <div className='px-[10px] sm:px-[0px] w-full sm:w-[540px] md:w-[660px] lg:w-[883px] min-h-[360px] flex flex-col gap-[60px]'>
                <div className=''>
                    <h2 className="mb-4">{localize('matches_search_title')}</h2>
                </div>
                <SearchByIdBlock />
                {/* <SearchByFiltersBlock /> */}
            </div >
        }
    />
}

const SearchByIdBlock: React.FC = () => {
    const [response, setResponse] = useState<ApiDataResponse<ShortMatchModel> | undefined>();
    const [matchId, setMatchId] = useState<number | undefined>();
    const repository = new MatchesRepository();

    function updateSearchValue(e: React.ChangeEvent<HTMLInputElement>) {
        setMatchId(Number(e.target.value));
    }

    return <div className="flex flex-col gap-[24px]">
        {/* <h2 className="text-[18px] lg:text-[28px] font-bold uppercase font-['Reaver'] text-start">
            {localize('matches_search_id_sub_title')}
        </h2> */}
        <div className="flex flex-row justify-between gap-[12px]">
            <SearchInput
                placeholderLocal="matches_search_id_input_placeholder"
                className="w-full"
                type="number"
                maxLength={2}
                onChange={updateSearchValue}
            />
            <OrangeButton
                localText="matches_search_id_btn"
                className="px-[24px] py-[14px] text-[14px] lg:text-[18px]"
                onClick={() => {
                    if (matchId)
                        repository.getShort(matchId)
                            .then(t => setResponse(t))
                }}
            />
        </div>
        {
            response
                ? response.hasErrors
                    ? <div className="flex flex-col justify-center text-center items-center gap-[8px]">
                        <span className="text-[18px] lg:text-[28px] font-semibold uppercase">
                            {localize("match_not_found_title")}
                        </span>
                    </div>
                    : <ShortMatchBlock match={response.data!} />
                : null
        }
    </div>
}

const SearchByFiltersBlock: React.FC = () => {
    const heroes = SubscribeGlobalState('heroes') ?? [];
    const repository = new MatchesRepository();

    const valuesOnPageCount = 20;

    const talentEmptyOption = {
        localName: 'matches_search_talent_empty_option',
        value: undefined
    };

    const heroesOptions = [{
        localName: 'matches_search_hero_empty_option',
        value: undefined
    }] as SelectOption[]


    const [filters, setFilters] = useState<MatchSearchFilters>({});
    const [talentOptions, setTalentOptions] = useState<SelectOption[]>([talentEmptyOption]);
    const [response, setResponse] = useState<ApiPaginationResponse<ShortMatchModel> | undefined>();
    const [page, setGamesPage] = useState(1);

    const handlePageChange = (page: number) => {
        setGamesPage(page);
        getPageValues(page);
    };

    function getPageValues(page: number) {
        repository.search(filters, page, valuesOnPageCount)
            .then(v => {
                if (v.hasErrors)
                    return console.log(v.errors);

                setResponse(v);
            });
    }

    heroes.forEach(h => heroesOptions.push({
        localName: h.userFriendlyName,
        value: h.name
    }))

    function updateSelectHero(value?: string): void {
        setFilters({
            ...filters,
            hero: value,
            talent: undefined
        })

        if (value) {
            const hero = heroes.find(f => f.name == value)

            if (hero) {
                const newtaletOptions = [
                    talentEmptyOption
                ] as SelectOption[]

                for (let index = 0; index < hero.talents.length; index++) {
                    newtaletOptions.push({
                        localName: hero.talents[index].replace('_', ' '),
                        value: hero.talents[index]
                    })
                }

                setTalentOptions(newtaletOptions);
            }
        }
    }

    function updateSelectTalent(value?: string): void {
        setFilters({
            ...filters,
            talent: value
        })
    }

    return <div className="flex flex-col gap-[24px]">
        <h2 className="text-[28px] font-bold uppercase font-['Reaver'] text-start">
            {localize('matches_search_filters_sub_title')}
        </h2>
        <div className="flex flex-row justify-between gap-[12px]">
            <SelectWithSearchInput
                value={filters.hero}
                options={heroesOptions} onChange={updateSelectHero} />
            <SelectWithSearchInput
                value={filters.talent}
                className=""
                options={talentOptions} onChange={updateSelectTalent} />
            <BlueButton
                disabled={!filters.hero}
                localText="matches_search_filter_btn"
                className=""
                onClick={() => getPageValues(1)}
            />
        </div>
        {
            response && <div>
                {response.data.values.map((match, i) => <ShortMatchBlock key={i} match={match} />)}
            </div>
        }
        {
            response && ((response?.data?.totalCount ?? 0) <= valuesOnPageCount)
                ? <PaginationButtons
                    className="mt-[32px]"
                    response={response}
                    currentPage={page}
                    onClick={handlePageChange}
                />
                : null
        }
    </div>
}

const ShortMatchBlock: React.FC<{
    match: ShortMatchModel
}> = ({ match }) => {
    const titleClassName = "text-[14px] uppercase font-['Roboto'] text-gray mb-[8px]"
    const textClassName = "text-[12px] lg:text-[24px] font-semibold"

    return <div className="flex flex-col gap-y-[32px] ">
        <div className="flex flex-row justify-between gap-[18px] text-16 ">
            <div>
                <div className={`${titleClassName}`}>
                    {localize("match_map_name")}
                </div>
                <div className={`${textClassName}`}>
                    {match.mapName}
                </div>
            </div>
            <div>
                <div className={`${titleClassName}`}>
                    {localize("match_heroes")}
                </div>
                <div className={`grid grid-cols-${match.isDuo ? '4' : '3'} gap-[4px]`}>
                    {match.heroes.map((h, i) => <HeroImage className="h-[20px] sm:h-[30px] lg:h-[40px]" heroName={h} size={"80x47"} />)}
                </div>
            </div>
            <div>
                <div className={`${titleClassName}`}>
                    {localize("match_created_date")}
                </div>
                <div className={`${textClassName}`}>
                    {toDateWithTime(match.createdAtUtc)}
                </div>
            </div>
            <div>
                <div className={`${titleClassName}`}>
                    {localize("match_status_title")}
                </div>
                <div className={`${textClassName}`}>
                    {(localize(`match_status_${match.isFinished}`))}
                </div>
            </div>
        </div>
        <BlueButton
            textClassName="text-[14px] lg:text-[18px]"
            localText="match_search_profile_btn"
            onClick={() => {
                window.location.href = `/matches/${match.matchId}`
            }}
        />
    </div>
}